import classNames from 'classnames'
import { Link } from 'gatsby-link'
import React, { FC } from 'react'
import { isNullOrWhiteSpace } from '../utilities/string'
import { Image, ImageProps } from './Image'
import { PlaceholderImage } from './PlaceholderImage'

export enum PersonLinkHoverBgColor {
  Variant1 = 'group-hover:bg-people-1',
  Variant2 = 'group-hover:bg-people-2',
  Variant3 = 'group-hover:bg-people-3',
  Variant4 = 'group-hover:bg-people-4',
  Variant5 = 'group-hover:bg-people-5',

  Variant1Faded = 'group-hover:bg-people-1 group-hover:bg-opacity-25',
  Variant2Faded = 'group-hover:bg-people-2 group-hover:bg-opacity-25',
}

export type Variant = 'normal' | 'large' | 'single-small' | 'team-list'

export interface PersonLinkProps {
  additionalClassName?: string
  advisor?: boolean
  country?: string
  gaTrackingId?: string
  hoverBgColor?: PersonLinkHoverBgColor
  image?: ImageProps | null
  name: string
  title: string
  url: string
  variant?: Variant
}

export const PersonLink: FC<PersonLinkProps> = ({
  additionalClassName,
  advisor,
  country,
  gaTrackingId,
  hoverBgColor,
  image,
  name,
  title,
  url,
  variant = 'normal',
}) => {
  let portraitSizeClassName: string
  let boxWidthClassName: string
  let topTextClassName = 'font-t-bold text-t-400 text-secondary-dark xxl:text-t-600'
  let bottomTextClassName = 'font-t-light text-t-300 text-secondary-darker xxl:text-t-500'

  switch (variant) {
    case 'large':
      portraitSizeClassName = 'w-150 h-150 xxl:w-240 xxl:h-240'
      boxWidthClassName = 'flex w-240 xxl:w-260'
      break
    case 'single-small':
      portraitSizeClassName = 'w-120 h-120'
      boxWidthClassName = 'inline-flex max-w-240 xxl:max-w-260'
      break
    case 'team-list':
      portraitSizeClassName = 'w-full h-150 tb:h-150 xxl:h-240'
      boxWidthClassName = 'inline-flex w-150 tb:w-150 xxl:w-240'

      topTextClassName =
        'font-t-bold text-secondary-dark text-t-400 tb:text-t-500 xxl:text-t-600'
      bottomTextClassName =
        'font-t-light text-secondary-darker text-t-300 tb:text-t-400 xxl:text-t-500'
      break
    case 'normal':
    default:
      portraitSizeClassName = 'w-120 h-120 sm:w-150 sm:h-150 xxl:w-240 xxl:h-240'
      boxWidthClassName = 'flex w-150 sm:w-240 xxl:w-240'
  }

  const figureClassName = classNames(
    'relative overflow-hidden mb-15 rounded group-focus:ring group-focus:ring-primary/40 xxl:mb-30',
    portraitSizeClassName
  )

  const imageHoverArea: JSX.Element = (
    <div
      aria-hidden
      className={classNames(
        'absolute top-0 left-0 w-full h-full bg-shade-lightest-warm blend-multiply transition-hover',
        hoverBgColor
      )}
    />
  )

  return (
    <Link
      className={classNames(
        'flex-col items-start group focus:outline-none',
        boxWidthClassName,
        additionalClassName
      )}
      data-ga-tracking-id={gaTrackingId}
      data-ga-tracking-name={!isNullOrWhiteSpace(gaTrackingId) ? title : undefined}
      to={url}
    >
      {!!image?.src?.length ? (
        <Image
          {...image}
          className={figureClassName}
          imgClassName="object-cover h-full w-full"
          loading="lazy"
        >
          {imageHoverArea}
        </Image>
      ) : (
        <figure className={figureClassName}>
          <PlaceholderImage />

          {imageHoverArea}
        </figure>
      )}

      <b className={topTextClassName}>{name}</b>

      <small
        className={classNames(
          variant === 'team-list' ? 'mt-3 tb:mt-10' : 'mt-15 sm:mt-5 md:mt-8',
          bottomTextClassName
        )}
      >
        {title}
      </small>

      {advisor && !isNullOrWhiteSpace(country) && (
        <small className={classNames('mt-8 sm:mt-5 md:mt-8', bottomTextClassName)}>
          {country}
        </small>
      )}
    </Link>
  )
}
